import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaArrowRight, FaCheckCircle, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaUserShield, FaPassport, FaChartLine, FaFacebookF } from "react-icons/fa";

// Components
import HeroCarousel from "../components/HeroCarousel";
import LogoMarquee from "../components/LogoMarquee";

// Images
import about2 from "../assets/about-2.png.jpg";
import about3 from "../assets/about-3.jpg";
import personal from "../assets/personal.jpg";
import education from "../assets/education.jpeg";
import business from "../assets/business.jpg";

export default function Home() {
  const { hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    }
  }, [hash]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="home-container" style={{ overflowX: "hidden", background: "#f8fafc" }}>

      {/* 1. HERO CAROUSEL */}
      <section id="home">
        <HeroCarousel />
      </section>

      {/* 2. LOGO MARQUEE */}
      <LogoMarquee />

      {/* 3. ABOUT SECTION */}
      <section id="about" className="section-padding" style={{ padding: "80px 0", background: "white" }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div className="about-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '60px', alignItems: 'center' }}>
            {/* Image Grid */}
            <motion.div
              className="about-images"
              style={{ position: 'relative', height: '500px' }}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <div style={{ position: 'absolute', top: 0, left: 0, width: '70%', height: '80%', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
                <img src={about2} alt="About RAMB" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ position: 'absolute', bottom: 0, right: 0, width: '60%', height: '60%', borderRadius: '20px', overflow: 'hidden', border: '8px solid white', boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}>
                <img src={about3} alt="Team" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{
                position: 'absolute',
                bottom: '40px',
                left: '-20px',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                padding: '20px',
                borderRadius: '16px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minWidth: '120px'
              }}>
                <span style={{ fontSize: '32px', fontWeight: '800', color: 'var(--blue2)', lineHeight: 1 }}>15+</span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', textAlign: 'center' }}>Years of<br />Excellence</span>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              className="about-content"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <span style={{ fontSize: '14px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--blue2)', display: 'block', marginBottom: '10px' }}>Who We Are</span>
              <h2 style={{ fontSize: '36px', fontWeight: '800', color: '#0f172a', marginBottom: '20px', lineHeight: 1.2 }}>Reliable Asset Management & Business</h2>
              <p style={{ fontSize: '16px', lineHeight: 1.7, color: '#475569', marginBottom: '30px' }}>
                At RAMB, we believe in empowering your financial journey. Whether you need a personal loan,
                business capital, or educational support, our tailored solutions are designed to meet your unique needs.
                We are committed to transparency, integrity, and your long-term success.
              </p>

              <div className="features-list" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '40px' }}>
                <div className="feature-item" style={{ display: 'flex', gap: '15px' }}>
                  <FaCheckCircle style={{ color: 'var(--blue2)', fontSize: '24px', flexShrink: 0 }} />
                  <div>
                    <h4 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>Fast Approval</h4>
                    <p style={{ fontSize: '14px', color: '#64748b' }}>Get your loans approved within 24 hours.</p>
                  </div>
                </div>
                <div className="feature-item" style={{ display: 'flex', gap: '15px' }}>
                  <FaCheckCircle style={{ color: 'var(--blue2)', fontSize: '24px', flexShrink: 0 }} />
                  <div>
                    <h4 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>Low Interest Rates</h4>
                    <p style={{ fontSize: '14px', color: '#64748b' }}>Competitive rates tailored to your budget.</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate("/loans")}
                style={{
                  padding: '14px 32px',
                  background: 'var(--blue2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.transform = "translateY(-2px)"}
                onMouseLeave={(e) => e.target.style.transform = "none"}
              >
                Discover More <FaArrowRight />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. SERVICES SECTION */}
      <section id="services" className="section-padding" style={{ padding: "100px 0", background: "#f1f5f9" }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <motion.div
            className="section-header center"
            style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 60px' }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <span style={{ fontSize: '14px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--blue2)', display: 'block', marginBottom: '10px' }}>Our Services</span>
            <h2 style={{ fontSize: '36px', fontWeight: '800', color: '#0f172a', marginBottom: '20px' }}>Financial Solutions for Everyone</h2>
            <p style={{ fontSize: '16px', lineHeight: 1.7, color: '#475569' }}>
              We offer a wide range of services to help you achieve your financial goals with ease and confidence.
              Explore our comprehensive loan and support packages.
            </p>
          </motion.div>

          <motion.div
            className="services-grid"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <ServiceCard
              image={personal}
              title="Personal Loans"
              desc="Quick cash for your personal needs with flexible repayment plans designed for your lifestyle."
              link="/loans"
            />
            <ServiceCard
              image={business}
              title="Business Loans"
              desc="Expand your business with our customized capital solutions. Grow your enterprise with RAMB."
              link="/loans"
            />
            <ServiceCard
              image={education}
              title="Education Support"
              desc="Invest in your future with our student-friendly financial aid. Your education is our priority."
              link="/education-support"
            />
          </motion.div>
        </div>
      </section>

      {/* 5. CTA SECTION */}
      <section className="cta-section" style={{ position: 'relative', padding: '100px 0', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', zIndex: 1 }}></div>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url("https://www.transparenttextures.com/patterns/cubes.png")', opacity: 0.1, zIndex: 2 }}></div>

        <div className="container" style={{ position: 'relative', zIndex: 10, maxWidth: '800px', margin: '0 auto', textAlign: 'center', color: 'white' }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 style={{ fontSize: '42px', fontWeight: '800', marginBottom: '20px' }}>Ready to Start Your Journey?</h2>
            <p style={{ fontSize: '18px', opacity: 0.9, marginBottom: '40px', lineHeight: 1.6 }}>
              Contact us today and let us help you build a secure financial future.
              Our experts are ready to guide you every step of the way.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  const el = document.getElementById('contact');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{
                  padding: '16px 36px',
                  background: 'white',
                  color: 'var(--blue2)',
                  fontSize: '16px',
                  fontWeight: '700',
                  border: 'none',
                  borderRadius: '50px',
                  cursor: 'pointer',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.transform = "translateY(-3px)"}
                onMouseLeave={(e) => e.target.style.transform = "none"}
              >
                Get Started Now
              </button>
              <button
                onClick={() => navigate('/loans')}
                style={{
                  padding: '16px 36px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '700',
                  borderRadius: '50px',
                  cursor: 'pointer',
                  backdropFilter: 'blur(5px)',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = "rgba(255,255,255,0.2)"}
                onMouseLeave={(e) => e.target.style.background = "rgba(255,255,255,0.1)"}
              >
                View Services
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TEAM (FLYER) */}
      <section id="team" className="section-padding" style={{ padding: "100px 0", background: "#f1f5f9" }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <motion.div
            className="home-team-box"
            style={{
              background: 'linear-gradient(135deg, white 0%, #f8fafc 100%)',
              borderRadius: '24px',
              padding: '60px',
              boxShadow: '0 20px 50px rgba(0,0,0,0.05)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div style={{ position: 'absolute', top: '-50%', left: '-10%', width: '300px', height: '300px', background: 'var(--blue1)', opacity: 0.1, borderRadius: '50%', filter: 'blur(50px)' }}></div>
            <div style={{ position: 'absolute', bottom: '-50%', right: '-10%', width: '300px', height: '300px', background: 'var(--blue2)', opacity: 0.1, borderRadius: '50%', filter: 'blur(50px)' }}></div>

            <div className="team-flyer-inner">
              {/* Left Image Side */}
              <div className="team-flyer-image">
                <img src={about3} alt="Team" />
                <div className="team-flyer-overlay"></div>
              </div>
              {/* Right Content */}
              <div className="team-flyer-content">
                <span style={{ fontSize: '14px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '2px', color: 'var(--blue2)', marginBottom: '16px' }}>Meet Our Team</span>
                <h2 className="team-flyer-title">The Minds Behind RAMB</h2>
                <p className="team-flyer-desc">
                  Our success is driven by a team of experienced leaders, dedicated advisors, and passionate professionals committed to securing your financial future.
                </p>
                <div>
                  <button
                    onClick={() => navigate('/our-team')}
                    style={{
                      padding: '14px 32px',
                      background: 'var(--blue2)',
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: '700',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      boxShadow: '0 10px 20px rgba(37,99,235,0.3)',
                      transition: 'transform 0.2s',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = "translateY(-3px)"}
                    onMouseLeave={(e) => e.target.style.transform = "none"}
                  >
                    View Corporate Structure <FaArrowRight />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 5.5 OUR SOCIAL MEDIAS SECTION */}
      <section id="social-media" className="section-padding" style={{ padding: "80px 0", background: "white" }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <motion.div
            className="section-header center"
            style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 50px' }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <span style={{ fontSize: '14px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--blue2)', display: 'block', marginBottom: '10px' }}>Connect With Us</span>
            <h2 style={{ fontSize: '36px', fontWeight: '800', color: '#0f172a', marginBottom: '20px' }}>Our Social Medias</h2>
            <p style={{ fontSize: '16px', lineHeight: 1.7, color: '#475569' }}>
              Stay updated and connect with our various organizations and branches through our official Facebook pages.
            </p>
          </motion.div>

          <motion.div
            className="social-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '24px'
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <SocialCard
              name="RAMB"
              url="https://www.facebook.com/share/17h5SbTpYV/?mibextid=wwXIfr"
            />
            <SocialCard
              name="அக் ஷயா மழலைகள் இல்லம் | Kilinochchi"
              url="https://www.facebook.com/share/1FXHppXGJb/?mibextid=wwXIfr"
            />
            <SocialCard
              name="RAMB Global VISA"
              url="https://www.facebook.com/share/1LEswdB3GZ/?mibextid=wwXIfr"
            />
            <SocialCard
              name="UK Global Education"
              url="https://www.facebook.com/share/1DWcGD5ZmK/?mibextid=wwXIfr"
            />
            <SocialCard
              name="Overseas Education | London"
              url="https://www.facebook.com/share/173jKqecx2/?mibextid=wwXIfr"
            />
            <SocialCard
              name="Picku Jaffna"
              url="https://www.facebook.com/share/1CVsQSS56K/?mibextid=wwXIfr"
            />
          </motion.div>
        </div>
      </section>

      {/* 6. CONTACT SECTION */}
      <section id="contact" className="section-padding" style={{ padding: "100px 0", background: "white" }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div className="contact-wrapper" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '60px' }}>
            <motion.div
              className="contact-info"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <span style={{ fontSize: '14px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--blue2)', display: 'block', marginBottom: '10px' }}>Contact Us</span>
              <h2 style={{ fontSize: '36px', fontWeight: '800', color: '#0f172a', marginBottom: '20px' }}>Get In Touch</h2>
              <p style={{ fontSize: '16px', color: '#475569', marginBottom: '40px', lineHeight: 1.6 }}>
                Have questions? We are here to help you via phone, email, or visit our office.
                Reach out to us for any inquiries about our loans or services.
              </p>

              <div className="contact-details" style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                <ContactRow icon={<FaPhoneAlt />} title="Call Us" text="021-222-4220" />
                <ContactRow icon={<FaEnvelope />} title="Email Us" text="info@ramb.lk" />
                <ContactRow icon={<FaMapMarkerAlt />} title="Visit Us" text="123 Main Street, Jaffna, Sri Lanka" />
              </div>
            </motion.div>

            <motion.div
              className="contact-form-card"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              style={{
                padding: '40px',
                background: 'white',
                borderRadius: '20px',
                boxShadow: '0 20px 50px rgba(0,0,0,0.08)',
                border: '1px solid #f1f5f9'
              }}
            >
              <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '30px', color: '#1e293b' }}>Send a Message</h3>
              <form style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div className="form-group">
                  <input type="text" placeholder="Your Name" style={inputStyle} />
                </div>
                <div className="form-group">
                  <input type="email" placeholder="Your Email" style={inputStyle} />
                </div>
                <div className="form-group">
                  <input type="text" placeholder="Subject" style={inputStyle} />
                </div>
                <div className="form-group">
                  <textarea placeholder="Message" rows="4" style={{ ...inputStyle, resize: 'vertical' }}></textarea>
                </div>
                <button
                  type="button"
                  style={{
                    padding: '16px',
                    background: 'var(--blue2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => e.target.style.background = "#1d4ed8"}
                  onMouseLeave={(e) => e.target.style.background = "var(--blue2)"}
                >
                  Send Message
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '14px',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  background: '#f8fafc',
  fontSize: '15px',
  color: '#334155',
  outline: 'none',
  transition: 'border-color 0.2s'
};

function ContactRow({ icon, title, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
      <div style={{
        width: '50px',
        height: '50px',
        borderRadius: '12px',
        background: 'rgba(37, 99, 235, 0.1)',
        color: 'var(--blue2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px',
        flexShrink: 0
      }}>
        {icon}
      </div>
      <div>
        <h5 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>{title}</h5>
        <p style={{ fontSize: '15px', color: '#64748b' }}>{text}</p>
      </div>
    </div>
  )
}

function ServiceCard({ image, title, desc, link }) {
  const navigate = useNavigate();
  return (
    <motion.div
      className="service-card"
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
      }}
      style={{
        background: 'white',
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        cursor: 'pointer'
      }}
      whileHover={{ y: -10, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
      onClick={() => navigate(link)}
    >
      <div className="card-img" style={{ height: '220px', overflow: 'hidden' }}>
        <img src={image} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s' }} />
      </div>
      <div className="card-body" style={{ padding: '30px' }}>
        <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>{title}</h3>
        <p style={{ fontSize: '15px', color: '#64748b', lineHeight: 1.6, marginBottom: '20px' }}>{desc}</p>
        <button style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--blue2)',
          fontWeight: '700',
          fontSize: '15px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          Read More <FaArrowRight size={12} />
        </button>
      </div>
    </motion.div>
  );
}

function SocialCard({ name, url }) {
  return (
    <motion.a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '24px',
        background: '#f8fafc',
        borderRadius: '16px',
        textDecoration: 'none',
        border: '1px solid #e2e8f0',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
      }}
      whileHover={{
        y: -5,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        borderColor: '#bfdbfe',
        background: 'white'
      }}
    >
      <div style={{
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        background: '#1877F2', // Real Facebook Blue
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '24px',
        flexShrink: 0,
        marginRight: '20px',
        boxShadow: '0 4px 10px rgba(24, 119, 242, 0.3)'
      }}>
        <FaFacebookF />
      </div>
      <div style={{ flex: 1 }}>
        <h4 style={{
          fontSize: '16px',
          fontWeight: '700',
          color: '#1e293b',
          margin: 0,
          lineHeight: '1.4'
        }}>
          {name}
        </h4>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginTop: '6px',
          color: '#3b82f6',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          Visit Page <FaArrowRight size={10} />
        </div>
      </div>
    </motion.a>
  );
}
